import { addToCart } from '@theme/cart-api';
import { Component } from '@theme/component';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * @typedef {Object} ProductCardRefs
 * @property {HTMLFormElement} [quickForm] Quick add form (sizes, or the hidden single-variant form)
 * @property {HTMLButtonElement} [toggle] "Quick add" bar that shows the sizes
 * @property {HTMLButtonElement[]} [sizes]
 * @property {HTMLImageElement} [image] Main product image
 * @property {HTMLElement} [vendor]
 * @property {HTMLAnchorElement} [titleLink]
 * @property {HTMLElement} [price]
 */

/**
 * @typedef {Object} QuickAddProduct What the phone quick add sheet shows for a card
 * @property {string} image Image URL, empty when the product has none
 * @property {string} vendor
 * @property {string} title
 * @property {string} url
 * @property {HTMLElement | undefined} price
 * @property {HTMLFormElement} form Sizes form, cloned into the sheet
 * @property {HTMLElement | null} opener Element that gets focus back when the sheet closes
 */

/**
 * @typedef {HTMLElement & { openFor(product: QuickAddProduct): void }} QuickAddSheetElement
 */

/** How long the "Aggiunto" state stays before the card resets */
const ADDED_DURATION = 1600;

/**
 * Product card quick add (snippets/product-card.liquid). The "Quick add" bar toggles a sizes panel
 * over the image (on phones it opens the `#quick-add-sheet` bottom sheet instead); a size, or the
 * bar of a single-variant product, adds one piece to the cart and the cart drawer opens.
 *
 * @extends {Component<ProductCardRefs>}
 */
class ProductCard extends Component {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #resetTimer;

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#resetTimer);
    document.removeEventListener('click', this.#onDocumentClick);
  }

  /** @param {MouseEvent} event */
  toggleQuickAdd(event) {
    const { quickForm, toggle } = this.refs;
    if (!quickForm || !toggle) return;
    if (isMobileBreakpoint() && this.#openSheet(quickForm, toggle)) return;

    const open = !this.classList.contains('is-quick');
    this.#setQuickOpen(open);
    // Keyboard users land on the first size once the panel is visible
    if (open && event.detail === 0) {
      const first = (this.refs.sizes || []).find((button) => !button.disabled);
      if (first) setTimeout(() => first.focus({ preventScroll: true }), 30);
    }
  }

  /** @param {SubmitEvent} event */
  async addToCart(event) {
    event.preventDefault();
    const button = event.submitter;
    if (!(button instanceof HTMLButtonElement) || button.name !== 'id' || button.disabled) return;

    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    const sections = await addToCart([{ id: Number(button.value), quantity: 1 }], { source: 'product-card' });
    button.classList.remove('is-loading');
    button.removeAttribute('aria-busy');
    if (!sections) return;

    const form = this.refs.quickForm;
    form?.classList.add('is-done');
    this.classList.add('is-added');
    clearTimeout(this.#resetTimer);
    this.#resetTimer = setTimeout(() => {
      form?.classList.remove('is-done');
      this.classList.remove('is-added');
      this.#setQuickOpen(false);
    }, ADDED_DURATION);
  }

  /** @param {boolean} open */
  #setQuickOpen(open) {
    this.classList.toggle('is-quick', open);
    this.refs.toggle?.setAttribute('aria-expanded', String(open));
    if (open) document.addEventListener('click', this.#onDocumentClick);
    else document.removeEventListener('click', this.#onDocumentClick);
  }

  /**
   * Any click outside a quick add form closes the open sizes panel (other cards included).
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('.product-card__quick')) return;
    if (this.refs.toggle?.contains(target)) return;
    this.#setQuickOpen(false);
  };

  /**
   * @param {HTMLFormElement} form
   * @param {HTMLElement} opener
   * @returns {boolean} Whether the sheet opened
   */
  #openSheet(form, opener) {
    const sheet = /** @type {QuickAddSheetElement | null} */ (document.getElementById('quick-add-sheet'));
    if (!sheet || typeof sheet.openFor !== 'function') return false;

    const { image, vendor, titleLink, price } = this.refs;
    sheet.openFor({
      image: image ? image.currentSrc || image.src : '',
      vendor: vendor?.textContent || '',
      title: titleLink?.textContent?.trim() || '',
      url: titleLink?.href || '#',
      price,
      form,
      opener,
    });
    return true;
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}
